import Button from "core/components/Button";
import Modal from "core/components/Modal";
import Search from "core/components/Field/Search";
import { useMemo, useState } from "react";
import ButtonLink from "core/components/ButtonLink";
import Typography from "core/components/Typography";
import { COMPONENTS } from "../utilities.jsx";
import { SkeletonAccordion } from "./UtilityComponents.jsx";
import { FIELDS, DEPLOYMENT_STATUSES } from "../constants.js";

import {
  Card,
  ProductBundle,
  Product,
  SectionSeparator,
  ProductHeader,
  ProductAttributes,
  ModalHeader,
  List,
  ListItem,
  ProductTitle,
  SummaryChanges,
} from "../styles.js";
import useBundleFilters from "core/hooks/useBundleFilters";

const ReviewSummary = ({
  bundles,
  configuredBundles,
  products,
  productsEnabled,
  getDynamicProduct,
  isLoading,
  deploymentStatus,
  isDirty,
}) => {
  const [moreTitle, setMoreTitle] = useState(null);
  const [moreItems, setMoreItems] = useState(null);
  const [moreSearch, setMoreSearch] = useState("");
  const { selectedBundleIds } = useBundleFilters();

  const modalItems = useMemo(() => {
    return moreItems && moreSearch
      ? moreItems.filter((item) =>
          (item.label || item).toLowerCase().includes(moreSearch.toLowerCase())
        )
      : moreItems;
  }, [moreItems, moreSearch]);

  const showMoreItems = (title, items) => {
    setMoreTitle(title);
    setMoreItems(items);
  };

  const hideMoreItems = () => {
    setMoreTitle(null);
    setMoreItems(null);
    setMoreSearch("");
  };

  const isDefaultStatus =
    deploymentStatus && deploymentStatus === DEPLOYMENT_STATUSES.DEFAULT;

  const bundlesToDisplay =
    isDefaultStatus || (!isDefaultStatus && isDirty)
      ? bundles
      : configuredBundles;

  if (isLoading) {
    return <SkeletonAccordion itemCount={bundlesToDisplay.length} />;
  }

  return (
    <div data-test="review-summary">
      {Object.values(bundlesToDisplay).map((bundle) => {
        const bundleProducts = Object.values(products).filter((product) =>
          isDefaultStatus || (!isDefaultStatus && isDirty)
            ? product.meta.bundle.id === bundle.id
            : product.meta.bundle.id === bundle.id &&
              product.data.enabled === true
        );

        const isBundleSelected = selectedBundleIds.includes(bundle.id);

        const bundleTitle = bundle.name;

        return (
          <Card key={bundle.id} value={bundle.id} title={bundleTitle}>
            <ProductBundle style={{ height: 29 }}>{bundleTitle}</ProductBundle>

            {bundleProducts.map((product) => {
              const dynamicProduct = isDefaultStatus
                ? getDynamicProduct(product.id)
                : products.find((item) => item.id === product.id);
              const reviewFields = FIELDS[bundle.id]?.[product.id];
              const enabled = productsEnabled.includes(product.id);
              const productLink = `../${bundle.path}/${product.page.path}`;

              return (
                <Product key={product.id}>
                  <SectionSeparator style={{ marginBottom: 16 }} />
                  <ProductHeader>
                    <ProductTitle>{product.meta.name}</ProductTitle>
                    {((isDefaultStatus && isBundleSelected) ||
                      (!isDefaultStatus && isDirty && isBundleSelected)) && (
                      <ButtonLink to={productLink} variant="text" size="small">
                        Edit
                      </ButtonLink>
                    )}
                  </ProductHeader>
                  <div style={{ color: "#000000" }}>
                    Status: {enabled ? "Licensed" : "Not Licensed"}
                  </div>
                  <ProductAttributes>
                    <SummaryChanges>
                      <div>
                        <div>
                          {reviewFields &&
                            Object.keys(reviewFields).map((key) => {
                              const Component = COMPONENTS[key];

                              return (
                                <div key={key} style={{ paddingBottom: 8 }}>
                                  <Component
                                    product={dynamicProduct}
                                    reviewFieldKey={reviewFields[key]}
                                    showMoreItems={showMoreItems}
                                    showNewChanges={true}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </SummaryChanges>
                  </ProductAttributes>
                </Product>
              );
            })}
          </Card>
        );
      })}
      <Modal visible={!!moreItems} dismiss={hideMoreItems}>
        <ModalHeader>{moreTitle}</ModalHeader>
        <Search
          styles={{ container: { width: "100%", marginBottom: "15px" } }}
          value={moreSearch}
          onChange={(value) => setMoreSearch(value)}
        />

        <List>
          {modalItems?.map((item, index) => {
            return (
              <ListItem key={index} index={index}>
                {item.label || item}
              </ListItem>
            );
          })}
        </List>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
          }}
        >
          <Button variant="outlined" width="143px" onClick={hideMoreItems}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ReviewSummary;
